import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { addDoc, collection, doc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { AlertCircle, ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';

// Request intake only. No client-side pricing, deposit collection, or scheduling commitments.
// Deployment prerequisite: restrict workOrders creates/updates in Firestore rules.
const SERVICES = [
  { id: 'network-infrastructure', name: 'Network Infrastructure' },
  { id: 'it-support', name: 'IT Support & Troubleshooting' },
  { id: 'cabling', name: 'Structured Cabling & Data Drops' },
  { id: 'equipment-installation', name: 'Equipment Installation' },
  { id: 'site-survey', name: 'Network / Site Assessment' },
  { id: 'security-systems', name: 'Security Camera & Access Systems' },
  { id: 'other', name: 'Other Business IT Service' },
];

const URGENCY = [
  { id: 'standard', label: 'Flexible scheduling' },
  { id: 'urgent', label: 'Within 1–2 business days (requested)' },
  { id: 'emergency', label: 'Urgent / business disruption (requested)' },
];

const todayLocal = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

export default function BookService() {
  const { currentUser, userProfile } = useAuth();
  const [form, setForm] = useState({
    serviceType: '',
    businessName: '',
    contactName: '',
    contactPhone: '',
    location: '',
    preferredDate: '',
    urgency: 'standard',
    description: '',
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [requestId, setRequestId] = useState('');
  const [assignedNumber,setAssignedNumber] = useState('');
  useEffect(()=>{
    if(!requestId) return;
    const unsubscribe=onSnapshot(doc(db,'workOrders',requestId),snapshot=>{
      if(snapshot.exists()){
        const number=snapshot.data().workOrderNumber;
        if(/^B0T\d{5}$/.test(number||'')) setAssignedNumber(number);
      }
    },error=>console.warn('Work order reference updates unavailable',error));
    return()=>unsubscribe();
  },[requestId]);

  const serviceName = useMemo(
    () => SERVICES.find(s => s.id === form.serviceType)?.name || '',
    [form.serviceType]
  );
  const setField = (name, value) => setForm(prev => ({ ...prev, [name]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (sending || requestId) return;
    setError('');

    if (!currentUser) {
      setError('Please sign in to submit a service request.');
      return;
    }
    if (userProfile?.role && userProfile.role !== 'client') {
      setError('This form is for client accounts.');
      return;
    }
    if (!serviceName || form.description.trim().length < 20 || !form.location.trim()) {
      setError('Choose a service and include the location and at least 20 characters of detail.');
      return;
    }
    if (form.preferredDate && form.preferredDate < todayLocal()) {
      setError('Choose today or a future date.');
      return;
    }

    setSending(true);
    try {
      const now = new Date().toISOString();
      // Only REQUEST data. A trusted administrator must determine prices,
      // issue quotes, authorize schedules, and record verified payments.
      const payload = {
        clientId: currentUser.uid,
        clientEmail: currentUser.email || '',
        clientName: form.contactName.trim() || userProfile?.name || currentUser.email || '',
        clientPhone: form.contactPhone.trim() || userProfile?.phone || '',
        clientCompany: form.businessName.trim() || userProfile?.company || '',
        title: `${serviceName} - ${form.location.trim()}`,
        serviceType: serviceName,
        location: form.location.trim(),
        preferredDate: form.preferredDate || null,
        urgency: form.urgency,
        description: form.description.trim(),
        status: 'pending',
        intakeType: 'service_request',
        createdAt: now,
        updatedAt: now,
      };
      const created = await addDoc(collection(db, 'workOrders'), payload);
      setRequestId(created.id);
    } catch (err) {
      console.error('Service request submission failed:', err);
      setError('We could not submit your request. Please email dispatch@chukologic.com or call (920) 724-1438.');
    } finally {
      setSending(false);
    }
  };

  if (requestId) {
    return (
      <div className="max-w-xl mx-auto px-4 py-12">
        <div className="rounded-2xl bg-white border border-gray-200 shadow-sm p-8 text-center">
          <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">Service request received</h1>
          <p className="mt-3 text-gray-600">Chuko Logic will review your requirements and contact you about next steps. Your preferred date is not a confirmed appointment.</p>
          <p className="mt-4 text-sm text-gray-500 break-all">Reference: {assignedNumber || 'Assigning reference…'}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
            <Link to={`/jobs/${requestId}`} className="px-5 py-3 rounded-lg bg-slate-900 text-white font-semibold">View request</Link>
            <Link to="/jobs" className="px-5 py-3 rounded-lg border border-gray-300 font-semibold text-gray-800">All requests</Link>
          </div>
          <p className="mt-6 text-sm text-gray-600">Need immediate assistance? <a className="underline" href="tel:+19207241438">(920) 724-1438</a></p>
        </div>
      </div>
    );
  }

  return (
    <div className="cl-book-service cl-page max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-gray-600 mb-5"><ArrowLeft size={16} /> Dashboard</Link>
      <h1 className="text-3xl font-bold text-gray-900">Request IT Service</h1>
      <p className="text-gray-600 mt-2 mb-7">Tell us what your business needs. We will assess the scope and send a written quote before work is authorized.</p>
      {error && <div role="alert" className="flex gap-2 items-start p-4 mb-5 rounded-lg border border-red-200 bg-red-50 text-red-800"><AlertCircle size={18} className="mt-0.5 shrink-0" />{error}</div>}

      <form onSubmit={submit} className="cl-form-card bg-white border border-gray-200 rounded-xl p-5 sm:p-8 shadow-sm space-y-5">
        <div>
          <label htmlFor="serviceType" className="block font-semibold text-gray-800 mb-1">Service needed *</label>
          <select id="serviceType" required value={form.serviceType} onChange={e => setField('serviceType', e.target.value)} className="w-full border border-gray-300 rounded-lg p-3">
            <option value="">Select a service</option>
            {SERVICES.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="businessName" className="block font-semibold text-gray-800 mb-1">Business name</label>
            <input id="businessName" maxLength={120} value={form.businessName} onChange={e => setField('businessName', e.target.value)} className="w-full border border-gray-300 rounded-lg p-3" placeholder={userProfile?.company || 'Company name'} />
          </div>
          <div>
            <label htmlFor="contactName" className="block font-semibold text-gray-800 mb-1">Contact name</label>
            <input id="contactName" maxLength={120} value={form.contactName} onChange={e => setField('contactName', e.target.value)} className="w-full border border-gray-300 rounded-lg p-3" placeholder={userProfile?.name || 'Your name'} />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="contactPhone" className="block font-semibold text-gray-800 mb-1">Contact phone</label>
            <input id="contactPhone" type="tel" maxLength={35} value={form.contactPhone} onChange={e => setField('contactPhone', e.target.value)} className="w-full border border-gray-300 rounded-lg p-3" placeholder={userProfile?.phone || '(###) ###-####'} />
          </div>
          <div>
            <label htmlFor="location" className="block font-semibold text-gray-800 mb-1">Service address or city *</label>
            <input id="location" required maxLength={220} value={form.location} onChange={e => setField('location', e.target.value)} className="w-full border border-gray-300 rounded-lg p-3" placeholder="Street address, city, state" />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="urgency" className="block font-semibold text-gray-800 mb-1">Urgency</label>
            <select id="urgency" value={form.urgency} onChange={e => setField('urgency', e.target.value)} className="w-full border border-gray-300 rounded-lg p-3">
              {URGENCY.map(u => <option key={u.id} value={u.id}>{u.label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="preferredDate" className="block font-semibold text-gray-800 mb-1">Preferred date (optional)</label>
            <input id="preferredDate" type="date" min={todayLocal()} value={form.preferredDate} onChange={e => setField('preferredDate', e.target.value)} className="w-full border border-gray-300 rounded-lg p-3" />
          </div>
        </div>
        <div>
          <label htmlFor="description" className="block font-semibold text-gray-800 mb-1">What needs to be done? *</label>
          <textarea id="description" required minLength={20} maxLength={4000} rows={5} value={form.description} onChange={e => setField('description', e.target.value)} className="w-full border border-gray-300 rounded-lg p-3" placeholder="Describe what is not working, affected devices or locations, and any deadlines." />
          <p className="text-xs text-gray-500 mt-1">Do not include passwords, payment card details, or other confidential credentials.</p>
        </div>
        <div className="bg-slate-50 p-4 rounded-lg text-sm text-gray-700">
          <strong>Before you submit:</strong> This is a service request, not a confirmed booking or price. Chuko Logic will provide a written scope and quote for your approval before beginning work. No payment is taken on this page.
        </div>
        <button disabled={sending} type="submit" className="cl-primary w-full flex items-center justify-center gap-2 rounded-lg px-6 py-3 bg-slate-900 text-white font-semibold disabled:opacity-50">
          {sending && <Loader2 className="animate-spin" size={18} />}
          {sending ? 'Submitting request…' : 'Submit Service Request'}
        </button>
        <p className="text-center text-sm text-gray-500">Questions? <a className="underline" href="mailto:dispatch@chukologic.com">dispatch@chukologic.com</a></p>
      </form>
    </div>
  );
}
